// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module TopPagesTable
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS top-pages table used by analytics
 * dashboards to compare the most-visited routes. The component includes
 * loading and empty states while preserving the standalone CSS class contract.
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from "@geenius/ui/solidjs-css";
import { type Component, For, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/** Summary row rendered by the SolidJS CSS top-pages table. */
export interface TopPageSummary {
  url: string;
  views: number;
  uniqueVisitors?: number;
  averageTimeMs?: number;
  averageTimeSeconds?: number;
  averageTime?: string;
}

function normalizeCount(value: number | undefined): number | undefined {
  return Number.isFinite(value) ? Math.max(0, value ?? 0) : undefined;
}

type AnalyticsCopy = ReturnType<typeof createAnalyticsCopy>;

function formatDuration(page: TopPageSummary, copy: AnalyticsCopy): string {
  if (page.averageTime) return page.averageTime;

  const milliseconds =
    page.averageTimeMs ??
    (page.averageTimeSeconds !== undefined
      ? page.averageTimeSeconds * 1_000
      : undefined);
  if (!Number.isFinite(milliseconds)) return "—";

  return copy.formatDuration(milliseconds ?? 0);
}

/**
 * Props for rendering the SolidJS CSS top-pages table.
 *
 * @property pages Ordered page summary rows with visit and engagement counts.
 * @property isLoading Whether the table should render placeholder rows.
 * @property class Optional class name merged into the table wrapper.
 */
export interface TopPagesTableProps {
  pages: TopPageSummary[];
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS CSS top-pages table with proportional view bars.
 *
 * @param props Table props containing page rows and optional loading state.
 * @returns A top-pages table element for analytics dashboards.
 */
export const TopPagesTable: Component<TopPagesTableProps> = (props) => {
  const safePages = () =>
    props.pages.map((page) => ({
      ...page,
      uniqueVisitors: normalizeCount(page.uniqueVisitors),
      views: Number.isFinite(page.views) ? Math.max(0, page.views) : 0,
    }));
  const maxViews = () =>
    safePages().length > 0
      ? Math.max(...safePages().map((page) => page.views), 1)
      : 1;
  const copy = createAnalyticsCopy();

  const progressLabel = (page: TopPageSummary): string =>
    copy.t("analytics.topPages.progressLabel", {
      url: page.url,
      views: copy.formatNumber(page.views),
    });

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardHeader>
          <CardTitle
            class="gn-analytics-top-pages__title"
            data-analytics-section-title
          >
            {copy.t("analytics.topPages.title")}
          </CardTitle>
        </CardHeader>
        <CardContent class="gn-analytics-top-pages">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Show
      when={!props.isLoading}
      fallback={
        <Card class={props.class}>
          <CardHeader>
            <CardTitle class="gn-analytics-top-pages__title">
              {copy.t("analytics.topPages.title")}
            </CardTitle>
          </CardHeader>
          <CardContent
            class={cx(
              "gn-analytics-top-pages",
              "gn-analytics-top-pages--loading",
            )}
            data-analytics-top-pages-loading
          >
            <For each={[1, 2, 3, 4, 5]}>
              {() => (
                <Skeleton
                  class="gn-analytics-top-pages__row-skeleton"
                  height="14px"
                />
              )}
            </For>
          </CardContent>
        </Card>
      }
    >
      <Card class={props.class}>
        <CardHeader>
          <CardTitle
            class="gn-analytics-top-pages__title"
            data-analytics-section-title
          >
            {copy.t("analytics.topPages.title")}
          </CardTitle>
        </CardHeader>
        <CardContent class="gn-analytics-top-pages" data-analytics-top-pages>
          <Show
            when={safePages().length > 0}
            fallback={<EmptyState title={copy.t("analytics.topPages.empty")} />}
          >
            <div
              class="gn-analytics-top-pages__header"
              data-analytics-top-pages-header
            >
              <span>{copy.t("analytics.topPages.columns.page")}</span>
              <span>{copy.t("analytics.topPages.columns.share")}</span>
              <span>{copy.t("analytics.topPages.columns.views")}</span>
              <span>{copy.t("analytics.dashboard.uniqueVisitors")}</span>
              <span>{copy.t("analytics.topPages.columns.averageTime")}</span>
            </div>
            <For each={safePages()}>
              {(page) => (
                <div
                  class="gn-analytics-top-pages__row"
                  data-analytics-page-row
                >
                  <span
                    class="gn-analytics-top-pages__url"
                    data-analytics-page-url
                  >
                    {page.url}
                  </span>
                  <div
                    class="gn-analytics-top-pages__bar-track"
                    data-analytics-page-bar-container
                  >
                    <div
                      aria-label={progressLabel(page)}
                      class="gui-progress gn-analytics-top-pages__bar"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuenow={page.views}
                      aria-valuemax={maxViews()}
                    >
                      <div
                        class="gui-progress__bar"
                        style={{
                          width: `${Math.min(100, (page.views / maxViews()) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <span
                    class="gn-analytics-top-pages__views"
                    data-analytics-page-views
                  >
                    {copy.formatNumber(page.views)}
                  </span>
                  <span
                    class="gn-analytics-top-pages__unique-visitors"
                    data-analytics-page-unique-visitors
                  >
                    {page.uniqueVisitors !== undefined
                      ? copy.formatNumber(page.uniqueVisitors)
                      : "—"}
                  </span>
                  <span
                    class="gn-analytics-top-pages__average-time"
                    data-analytics-page-average-time
                  >
                    {formatDuration(page, copy)}
                  </span>
                </div>
              )}
            </For>
          </Show>
        </CardContent>
      </Card>
    </Show>
  );
};
